import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Checkbox,
  TablePagination,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Chip,
} from '@mui/material';
import { listCorals, getCoralByRfid, getExport, modifyCoralAssignments } from '../services/api';
import { CoralResponse, ExportResponse } from '../types';
import { playErrorBeep } from '../utils/audio';

interface CoralAssignmentAction {
  action: 'add' | 'remove';
  corals: string[];
}

// Backend caps listCorals() at 200/page - the manual tab must page through
// the get-corals endpoint via cursor rather than assume one page is everything.
const AVAILABLE_CORALS_PAGE_SIZE = 50;

interface AssignCoralDialogProps {
  open: boolean;
  exportId: string;
  export?: ExportResponse;
  onClose: () => void;
  onSuccess: () => void;
}

const AssignCoralDialog: React.FC<AssignCoralDialogProps> = ({
  open,
  exportId,
  export: exportData,
  onClose,
  onSuccess,
}) => {
  // Assigned corals state
  const [assignedCorals, setAssignedCorals] = useState<CoralResponse[]>([]);
  const [assignedPage, setAssignedPage] = useState(0);
  const [assignedRowsPerPage, setAssignedRowsPerPage] = useState(5);
  const [coralsToRemove, setCoralsToRemove] = useState<Set<string>>(new Set());

  // Scanner state
  const [scannerInput, setScannerInput] = useState('');
  const [scannerChips, setScannerChips] = useState<string[]>([]);
  const [scanError, setScanError] = useState<string | null>(null);
  const scannerInputRef = useRef<HTMLInputElement | null>(null);

  // Manual entry state - server-side cursor pagination (table can hold thousands of corals)
  const [rawAvailableCorals, setRawAvailableCorals] = useState<CoralResponse[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [availableNextCursor, setAvailableNextCursor] = useState<string | null>(null);
  const [availableCursorHistory, setAvailableCursorHistory] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<'ALIVE' | 'ALL'>('ALIVE');

  // Tab state
  const [addTab, setAddTab] = useState(0); // 0 = scanner, 1 = manual
  const [coralsToAdd, setCoralsToAdd] = useState<Set<string>>(new Set());
  const [coralsToAddDetails, setCoralsToAddDetails] = useState<Map<string, CoralResponse>>(
    new Map()
  );

  // Filter client-side so checking/unchecking a coral doesn't refetch the current page
  const availableCorals = useMemo(
    () =>
      rawAvailableCorals.filter(
        (coral) =>
          !assignedCorals.some((ac) => ac.coralId === coral.coralId) &&
          !coralsToAdd.has(coral.coralId)
      ),
    [rawAvailableCorals, assignedCorals, coralsToAdd]
  );
  const [expandedAddDetails, setExpandedAddDetails] = useState(false);
  const [expandedRemoveDetails, setExpandedRemoveDetails] = useState(false);

  // Dialog state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Stepper state
  const steps = ['Review Current', 'Add Corals', 'Review Changes'];
  const [activeStep, setActiveStep] = useState(0);
  const handleNext = () => setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const loadAssignedCorals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log(`[AssignCoralDialog] Loading assigned corals for exportId: ${exportId}`);
      const exportResponse = await getExport(exportId);
      console.log('[AssignCoralDialog] Export response:', exportResponse);
      if (exportResponse.assignedCoral && Array.isArray(exportResponse.assignedCoral)) {
        // Handle array of coral objects
        const corals = exportResponse.assignedCoral.map((item) => {
          if (typeof item === 'object' && item.coralId) {
            // Already a coral object
            return item as CoralResponse;
          }
          // Fallback for string IDs (shouldn't happen with new API)
          const coralId = typeof item === 'string' ? item : '';
          return {
            coralId,
            rfid: coralId,
            speciesCode: 'Unknown',
            status: 'ALIVE' as const,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        });
        const msg = `[AssignCoralDialog] Loaded ${corals.length} assigned corals:`;
        console.log(msg, corals);
        setAssignedCorals(corals);
      } else {
        console.log('[AssignCoralDialog] No assigned corals found');
        setAssignedCorals([]);
      }
    } catch (err: any) {
      console.error('[AssignCoralDialog] Error loading assigned corals:', err);

      let errorMessage = 'Failed to load assigned corals for this export';

      if (err.response?.data?.errors) {
        errorMessage = `Loading failed: ${err.response.data.errors}`;
      } else if (err.response?.status === 404) {
        errorMessage = 'Export not found. Please verify the export ID and try again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. You may not have permission to view this export.';
      } else if (err.message) {
        errorMessage = `Loading failed: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [exportId]);

  // Load assigned corals when dialog opens
  useEffect(() => {
    if (open && exportId) {
      loadAssignedCorals();
      setCoralsToRemove(new Set());
      setCoralsToAdd(new Set());
      setScannerInput('');
      setScannerChips([]);
      setScanError(null);
      setAddTab(0); // Reset to scanner tab
      setError(null);
      setSuccess(null);
      setExpandedAddDetails(false);
      setExpandedRemoveDetails(false);
      setActiveStep(0);
      // Auto-focus the scanner input when dialog opens
      setTimeout(() => {
        scannerInputRef.current?.focus();
      }, 100);
    }
  }, [open, exportId, loadAssignedCorals]);

  // Auto-focus scanner when switching to scanner tab
  useEffect(() => {
    if (open && addTab === 0) {
      setTimeout(() => {
        scannerInputRef.current?.focus();
      }, 100);
    }
  }, [open, addTab]);

  const handleScannerInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toUpperCase();

    if (value.length < 24) {
      setScannerInput(value);
      setScanError(null);
      return;
    }

    const potentialRfids: string[] = [];
    const existingRfids = new Set(scannerChips);
    let currentString = '';
    let foundDuplicate = false;

    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      if (/[A-Z0-9]/.test(char)) {
        currentString += char;

        if (currentString.length === 24) {
          if (!existingRfids.has(currentString) && !potentialRfids.includes(currentString)) {
            potentialRfids.push(currentString);
            existingRfids.add(currentString);
          } else {
            foundDuplicate = true;
          }
          currentString = '';
        }
      } else {
        currentString = '';
      }
    }

    // Play error beep if any duplicates were found during scanning
    if (foundDuplicate) {
      playErrorBeep();
    }

    if (potentialRfids.length > 0) {
      setScannerChips((prev) => [...prev, ...potentialRfids]);
    }

    setScannerInput(currentString);
    setScanError(null);
  };

  const handleScanAdd = async () => {
    if (scannerChips.length === 0) {
      setScanError('No RFIDs ready to add. Scan or paste tags first.');
      return;
    }

    try {
      setScanError(null);
      console.log(
        `[AssignCoralDialog] Looking up ${scannerChips.length} scanned RFIDs via getCoralByRfid`
      );

      // Look up each scanned RFID directly (RfidIndex GSI) instead of scanning a capped list page
      const lookups = await Promise.allSettled(scannerChips.map((rfid) => getCoralByRfid(rfid)));

      const newSet = new Set(coralsToAdd);
      const newDetails = new Map(coralsToAddDetails);

      let addedCount = 0;
      const notFound: string[] = [];
      const alreadyAssigned: string[] = [];
      const alreadyQueued: string[] = [];
      const lookupFailed: string[] = [];

      lookups.forEach((result, index) => {
        const rfid = scannerChips[index];

        if (result.status === 'rejected') {
          console.error(`[AssignCoralDialog] getCoralByRfid failed for ${rfid}:`, result.reason);
          lookupFailed.push(rfid);
          return;
        }

        const coral = result.value;
        if (!coral) {
          notFound.push(rfid);
          return;
        }

        const assigned = assignedCorals.some((ac) => ac.coralId === coral.coralId);
        if (assigned) {
          alreadyAssigned.push(rfid);
          return;
        }

        if (newSet.has(coral.coralId)) {
          alreadyQueued.push(rfid);
          return;
        }

        newSet.add(coral.coralId);
        newDetails.set(coral.coralId, coral);
        addedCount += 1;
      });

      setCoralsToAdd(newSet);
      setCoralsToAddDetails(newDetails);
      setScannerChips([]);
      setScannerInput('');
      scannerInputRef.current?.focus();

      const messages: string[] = [];
      if (notFound.length > 0) {
        messages.push(`Not found: ${notFound.join(', ')}`);
      }
      if (alreadyAssigned.length > 0) {
        messages.push(`Already assigned: ${alreadyAssigned.join(', ')}`);
      }
      if (alreadyQueued.length > 0) {
        messages.push(`Already queued: ${alreadyQueued.join(', ')}`);
      }
      if (lookupFailed.length > 0) {
        messages.push(`Lookup failed (check console): ${lookupFailed.join(', ')}`);
      }

      // Play error beep if any issues were found
      if (messages.length > 0 || (addedCount === 0 && scannerChips.length > 0)) {
        playErrorBeep();
      }

      if (messages.length > 0) {
        setScanError(messages.join(' | '));
      } else {
        setScanError(null);
      }

      if (addedCount === 0 && messages.length === 0) {
        setScanError('No corals were added.');
      }
    } catch (err: any) {
      console.error('[AssignCoralDialog] Error scanning coral:', err);

      let errorMessage = 'Failed to scan coral';
      if (err.response?.data?.errors) {
        errorMessage = `Scan failed: ${err.response.data.errors}`;
      } else if (err.message) {
        errorMessage = `Scan failed: ${err.message}`;
      }

      setScanError(errorMessage);
    }
  };

  const handleRemoveScannerChip = (rfid: string) => {
    setScannerChips((prev) => prev.filter((chip) => chip !== rfid));
  };

  const handleClearScannerChips = () => {
    setScannerChips([]);
    setScannerInput('');
    scannerInputRef.current?.focus();
  };

  const loadAvailableCorals = useCallback(
    async (cursor?: string) => {
      try {
        setSearchLoading(true);
        const status = statusFilter === 'ALIVE' ? 'ALIVE' : undefined;
        console.log(
          '[AssignCoralDialog] Loading available corals page, statusFilter:',
          statusFilter,
          'cursor:',
          cursor || '(first page)'
        );
        const response = await listCorals(AVAILABLE_CORALS_PAGE_SIZE, cursor, status);
        console.log(
          `[AssignCoralDialog] Loaded ${response.items?.length ?? 0} corals, nextCursor:`,
          response.nextCursor
        );
        setRawAvailableCorals(response.items || []);
        setAvailableNextCursor(response.nextCursor || null);
      } catch (err: any) {
        console.error('[AssignCoralDialog] Error loading corals:', err);

        let errorMessage = 'Failed to load available corals';

        if (err.response?.data?.errors) {
          errorMessage = `Loading failed: ${err.response.data.errors}`;
        } else if (err.response?.status === 403) {
          errorMessage = 'Access denied. You may not have permission to view coral data.';
        } else if (err.message) {
          errorMessage = `Loading failed: ${err.message}`;
        }

        setError(errorMessage);
      } finally {
        setSearchLoading(false);
      }
    },
    [statusFilter]
  );

  const handleAddCoralsCheckbox = (coralId: string, coral?: CoralResponse) => {
    const newSet = new Set(coralsToAdd);
    if (newSet.has(coralId)) {
      newSet.delete(coralId);
      // Remove from details cache
      const newDetails = new Map(coralsToAddDetails);
      newDetails.delete(coralId);
      setCoralsToAddDetails(newDetails);
    } else {
      newSet.add(coralId);
      // Cache the coral details if provided
      if (coral) {
        const newDetails = new Map(coralsToAddDetails);
        newDetails.set(coralId, coral);
        setCoralsToAddDetails(newDetails);
      }
    }
    setCoralsToAdd(newSet);
  };

  const handleAvailableNextPage = () => {
    if (!availableNextCursor) return;
    setAvailableCursorHistory((prev) => [...prev, availableNextCursor]);
    loadAvailableCorals(availableNextCursor);
  };

  const handleAvailablePrevPage = () => {
    if (availableCursorHistory.length === 0) {
      loadAvailableCorals();
      return;
    }
    const newHistory = availableCursorHistory.slice(0, -1);
    const prevCursor = newHistory[newHistory.length - 1];
    setAvailableCursorHistory(newHistory);
    loadAvailableCorals(prevCursor);
  };

  const handleToggleRemove = (coralId: string) => {
    const newSet = new Set(coralsToRemove);
    if (newSet.has(coralId)) {
      newSet.delete(coralId);
      console.log(`[AssignCoralDialog] Unchecked coral for removal: ${coralId}`);
    } else {
      newSet.add(coralId);
      console.log(`[AssignCoralDialog] Checked coral for removal: ${coralId}`);
    }
    console.log(`[AssignCoralDialog] Current coralsToRemove:`, Array.from(newSet));
    setCoralsToRemove(newSet);
  };

  const handleAssignedPageChange = (event: unknown, newPage: number) => {
    setAssignedPage(newPage);
  };

  const handleAssignedRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAssignedRowsPerPage(parseInt(event.target.value, 10));
    setAssignedPage(0);
  };

  // Load available corals when manual tab opens or status filter changes; always restart at page 1
  useEffect(() => {
    if (addTab === 1) {
      setAvailableCursorHistory([]);
      loadAvailableCorals();
    }
  }, [addTab, statusFilter, loadAvailableCorals]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const actions: CoralAssignmentAction[] = [];

      // Add remove actions
      if (coralsToRemove.size > 0) {
        const removeAction = {
          action: 'remove' as const,
          corals: Array.from(coralsToRemove),
        };
        console.log('[AssignCoralDialog] Remove action:', removeAction);
        actions.push(removeAction);
      }

      // Add add actions
      if (coralsToAdd.size > 0) {
        const addAction = {
          action: 'add' as const,
          corals: Array.from(coralsToAdd),
        };
        console.log('[AssignCoralDialog] Add action:', addAction);
        actions.push(addAction);
      }

      if (actions.length === 0) {
        console.log('[AssignCoralDialog] No changes to apply');
        setSuccess('No changes to apply');
        return;
      }

      console.log('[AssignCoralDialog] Submitting API request with actions:', actions);
      console.log('[AssignCoralDialog] ExportId:', exportId);

      await modifyCoralAssignments(exportId, actions);
      console.log('[AssignCoralDialog] API response successful');

      setSuccess('Coral assignments updated successfully');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('[AssignCoralDialog] Error updating coral assignments:', err);

      let errorMessage = 'Failed to update coral assignments';

      // Extract detailed error information
      if (err.response?.data?.errors) {
        errorMessage = `Assignment failed: ${err.response.data.errors}`;
      } else if (err.response?.data?.message) {
        errorMessage = `Assignment failed: ${err.response.data.message}`;
      } else if (err.message) {
        errorMessage = `Assignment failed: ${err.message}`;
      }

      // Add context about what was being attempted
      const actionSummary: string[] = [];
      if (coralsToAdd.size > 0) {
        actionSummary.push(`adding ${coralsToAdd.size} coral(s)`);
      }
      if (coralsToRemove.size > 0) {
        actionSummary.push(`removing ${coralsToRemove.size} coral(s)`);
      }

      if (actionSummary.length > 0) {
        errorMessage += `\n\nAttempted action: ${actionSummary.join(' and ')}.`;
      }

      console.error('[AssignCoralDialog] Error details:', errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const startIndex = assignedPage * assignedRowsPerPage;
  const endIndex = startIndex + assignedRowsPerPage;
  // Display just the assigned corals (pending are tracked separately)
  const displayedAssignedCorals = assignedCorals;
  const paginatedAssignedCorals = displayedAssignedCorals.slice(startIndex, endIndex);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6">Manage Coral Assignments</Typography>
          {exportData && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {exportData.company} - {exportData.exportName || 'Unnamed Export'}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {error && (
          <Alert severity="error">
            <Box
              component="div"
              sx={{
                whiteSpace: 'pre-line', // Preserve line breaks
                fontFamily: 'inherit',
              }}
            >
              {error}
            </Box>
          </Alert>
        )}
        {success && (
          <Alert severity="success">
            <Box
              component="div"
              sx={{
                whiteSpace: 'pre-line', // Preserve line breaks
                fontFamily: 'inherit',
              }}
            >
              {success}
            </Box>
          </Alert>
        )}

        {/* Stepper */}
        {!loading && (
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <>
            {/* Step 0: Assigned Corals Section */}
            {activeStep === 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h6">
                    Assigned Corals ({displayedAssignedCorals.length})
                  </Typography>
                  {coralsToAdd.size > 0 && (
                    <Box
                      sx={{
                        bgcolor: 'success.light',
                        color: 'success.dark',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      {coralsToAdd.size} pending to add
                    </Box>
                  )}
                  {coralsToRemove.size > 0 && (
                    <Box
                      sx={{
                        bgcolor: 'error.light',
                        color: 'error.dark',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      {coralsToRemove.size} selected for removal
                    </Box>
                  )}
                </Box>

                {assignedCorals.length === 0 ? (
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography color="text.secondary" align="center">
                      No corals assigned to this export yet
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell padding="checkbox" sx={{ width: 50 }}>
                              <Checkbox
                                indeterminate={
                                  coralsToRemove.size > 0 &&
                                  coralsToRemove.size < paginatedAssignedCorals.length
                                }
                                checked={
                                  paginatedAssignedCorals.length > 0 &&
                                  paginatedAssignedCorals.every((c) =>
                                    coralsToRemove.has(c.coralId)
                                  )
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    const newSet = new Set(coralsToRemove);
                                    paginatedAssignedCorals.forEach((c) => newSet.add(c.coralId));
                                    setCoralsToRemove(newSet);
                                  } else {
                                    const newSet = new Set(coralsToRemove);
                                    paginatedAssignedCorals.forEach((c) =>
                                      newSet.delete(c.coralId)
                                    );
                                    setCoralsToRemove(newSet);
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>RFID</TableCell>
                            <TableCell>Species</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Cut Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedAssignedCorals.map((coral) => {
                            const isPending = coralsToAdd.has(coral.coralId);
                            const isMarkedForRemoval = coralsToRemove.has(coral.coralId);
                            return (
                              <TableRow
                                key={coral.coralId}
                                hover
                                sx={{
                                  bgcolor: isMarkedForRemoval
                                    ? 'error.lighter'
                                    : isPending
                                      ? 'success.lighter'
                                      : 'transparent',
                                  transition: 'background-color 0.2s',
                                }}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={isMarkedForRemoval}
                                    onChange={() => handleToggleRemove(coral.coralId)}
                                    disabled={isPending}
                                    sx={{
                                      '&.Mui-checked': {
                                        color: 'error.main',
                                      },
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{coral.rfid}</TableCell>
                                <TableCell>{coral.speciesCode}</TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: 'inline-block',
                                      px: 1,
                                      py: 0.5,
                                      bgcolor:
                                        coral.status === 'ALIVE'
                                          ? 'success.light'
                                          : coral.status === 'DECEASED'
                                            ? 'error.light'
                                            : 'warning.light',
                                      borderRadius: 1,
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {coral.status}
                                  </Typography>
                                </TableCell>
                                <TableCell>{coral.cutDate || '—'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {assignedCorals.length > assignedRowsPerPage && (
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={assignedCorals.length}
                        rowsPerPage={assignedRowsPerPage}
                        page={assignedPage}
                        onPageChange={handleAssignedPageChange}
                        onRowsPerPageChange={handleAssignedRowsPerPageChange}
                      />
                    )}

                    {coralsToRemove.size > 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {coralsToRemove.size} coral(s) selected for removal
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Will be unassigned from export and set to ALIVE status
                        </Typography>
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            )}

            {activeStep === 0 && <Divider />}

            {/* Step 2: Pending Changes Summary Section */}
            {activeStep === 2 && (coralsToAdd.size > 0 || coralsToRemove.size > 0) && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Pending Changes
                </Typography>

                {coralsToAdd.size > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        p: 1,
                        bgcolor: 'success.light',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'success.lighter' },
                      }}
                      onClick={() => setExpandedAddDetails(!expandedAddDetails)}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: 'success.dark', flex: 1 }}
                      >
                        + Adding {coralsToAdd.size} coral(s)
                      </Typography>
                      <Typography variant="caption" color="success.dark">
                        {expandedAddDetails ? '▼' : '▶'}
                      </Typography>
                    </Box>
                    {expandedAddDetails && (
                      <Box sx={{ pl: 2 }}>
                        {Array.from(coralsToAdd)
                          .map((coralId) => {
                            // First check the details cache, then look in assignedCorals and availableCorals
                            return (
                              coralsToAddDetails.get(coralId) ||
                              assignedCorals.find((c) => c.coralId === coralId) ||
                              availableCorals.find((c) => c.coralId === coralId)
                            );
                          })
                          .filter((c): c is CoralResponse => c !== undefined)
                          .map((coral) => (
                            <Box
                              key={coral.coralId}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                py: 1,
                                px: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:last-child': { borderBottom: 'none' },
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {coral.rfid}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                  {coral.cutDate || 'No date'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Species: {coral.speciesCode}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                                >
                                  ID: {coral.coralId.substring(0, 8)}...
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    )}
                  </Box>
                )}

                {coralsToRemove.size > 0 && (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        p: 1,
                        bgcolor: 'error.light',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'error.lighter' },
                      }}
                      onClick={() => setExpandedRemoveDetails(!expandedRemoveDetails)}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: 'error.dark', flex: 1 }}
                      >
                        − Removing {coralsToRemove.size} coral(s)
                      </Typography>
                      <Typography variant="caption" color="error.dark">
                        {expandedRemoveDetails ? '▼' : '▶'}
                      </Typography>
                    </Box>
                    {expandedRemoveDetails && (
                      <Box sx={{ pl: 2 }}>
                        {assignedCorals
                          .filter((coral) => coralsToRemove.has(coral.coralId))
                          .map((coral) => (
                            <Box
                              key={coral.coralId}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                py: 1,
                                px: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:last-child': { borderBottom: 'none' },
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {coral.rfid}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                  {coral.cutDate || 'No date'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Species: {coral.speciesCode}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                                >
                                  ID: {coral.coralId.substring(0, 8)}...
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {activeStep === 1 && <Divider />}
            {activeStep === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Add Corals to Export
                </Typography>

                <Tabs
                  value={addTab}
                  onChange={(_, value) => {
                    setAddTab(value);
                    setScannerInput('');
                    setScannerChips([]);
                    setScanError(null);
                  }}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  <Tab label="Scanner" />
                  <Tab label="Manual Search" />
                </Tabs>

                {/* Scanner Tab */}
                {addTab === 0 && (
                  <Box>
                    {scanError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {scanError}
                      </Alert>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <TextField
                        fullWidth
                        placeholder="Scan or paste RFIDs..."
                        value={scannerInput}
                        onChange={handleScannerInputChange}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleScanAdd();
                          }
                        }}
                        autoFocus
                        size="small"
                        inputRef={scannerInputRef}
                        helperText={
                          scannerChips.length > 0
                            ? `${scannerChips.length} RFID${scannerChips.length > 1 ? 's' : ''} ready - press Enter or Add All to queue`
                            : 'RFIDs will appear as chips below as you scan or paste them'
                        }
                      />
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleScanAdd}
                        // startIcon={<AddIcon />}
                        disabled={scannerChips.length === 0}
                        sx={{
                          height: '40px',
                          alignSelf: 'flex-start',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2,
                        }}
                      >
                        Add All
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleClearScannerChips}
                        disabled={scannerChips.length === 0 && !scannerInput}
                        sx={{
                          height: '40px',
                          alignSelf: 'flex-start',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2,
                        }}
                      >
                        Clear
                      </Button>
                    </Box>

                    {scannerChips.length > 0 && (
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                          minHeight: '40px',
                          maxHeight: '120px',
                          overflowY: 'auto',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          alignItems: 'flex-start',
                        }}
                      >
                        {scannerChips.map((rfid) => (
                          <Chip
                            key={rfid}
                            label={rfid}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onDelete={() => handleRemoveScannerChip(rfid)}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Manual Browse Tab */}
                {addTab === 1 && (
                  <Box>
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={statusFilter === 'ALL'}
                              onChange={(e) => setStatusFilter(e.target.checked ? 'ALL' : 'ALIVE')}
                            />
                          }
                          label="Show all coral (not just ALIVE)"
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, pl: 1 }}
                      >
                        {statusFilter === 'ALIVE'
                          ? 'Showing ALIVE corals only'
                          : 'Showing all coral statuses'}{' '}
                        · {AVAILABLE_CORALS_PAGE_SIZE} per page - use Previous/Next to browse more
                      </Typography>
                    </Box>

                    {searchLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    )}

                    {!searchLoading && availableCorals.length === 0 ? (
                      <Paper sx={{ p: 3, bgcolor: 'action.hover' }}>
                        <Typography color="text.secondary" align="center">
                          No corals available to add on this page
                        </Typography>
                      </Paper>
                    ) : (
                      !searchLoading && (
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell padding="checkbox" width={40}>
                                  <Checkbox
                                    size="small"
                                    indeterminate={
                                      availableCorals.some((c) => coralsToAdd.has(c.coralId)) &&
                                      !availableCorals.every((c) => coralsToAdd.has(c.coralId))
                                    }
                                    checked={
                                      availableCorals.length > 0 &&
                                      availableCorals.every((c) => coralsToAdd.has(c.coralId))
                                    }
                                    onChange={(event) => {
                                      const newSet = new Set(coralsToAdd);
                                      if (event.target.checked) {
                                        availableCorals.forEach((c) => newSet.add(c.coralId));
                                      } else {
                                        availableCorals.forEach((c) => newSet.delete(c.coralId));
                                      }
                                      setCoralsToAdd(newSet);
                                    }}
                                  />
                                </TableCell>
                                <TableCell>RFID</TableCell>
                                <TableCell>Species</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Cut Date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {availableCorals.map((coral) => (
                                <TableRow
                                  key={coral.coralId}
                                  hover
                                  sx={{
                                    bgcolor: coralsToAdd.has(coral.coralId)
                                      ? 'action.selected'
                                      : 'inherit',
                                  }}
                                >
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      size="small"
                                      checked={coralsToAdd.has(coral.coralId)}
                                      onChange={() => handleAddCoralsCheckbox(coral.coralId, coral)}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                    {coral.rfid}
                                  </TableCell>
                                  <TableCell>{coral.speciesCode}</TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        display: 'inline-block',
                                        px: 1,
                                        py: 0.5,
                                        bgcolor:
                                          coral.status === 'ALIVE'
                                            ? 'success.light'
                                            : coral.status === 'DECEASED'
                                              ? 'error.light'
                                              : 'warning.light',
                                        borderRadius: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {coral.status}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{coral.cutDate || '—'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )
                    )}

                    {!searchLoading && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 1,
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleAvailablePrevPage}
                          disabled={availableCursorHistory.length === 0}
                        >
                          Previous Page
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Page {availableCursorHistory.length + 1}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleAvailableNextPage}
                          disabled={!availableNextCursor}
                        >
                          Next Page
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {coralsToAdd.size > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {coralsToAdd.size} coral(s) ready to add
                  </Alert>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={submitting || loading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 && (
          <Button onClick={handleNext} variant="contained" disabled={loading}>
            Next
          </Button>
        )}
        {activeStep === steps.length - 1 && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              submitting || loading || (coralsToRemove.size === 0 && coralsToAdd.size === 0)
            }
          >
            {submitting ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
            Apply Changes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssignCoralDialog;
